import { requestProjectApi } from 'serviceWorker/api/requestApi';
import { broadcastSavedIssue } from 'serviceWorker/broadcasts/issues';
import * as issuesDb from 'serviceWorker/db/issues/issues';
import type { EventEntity } from 'serviceWorker/repository/issue/entity';
import type {
  IssueEventAddress,
  IssueEventDraft,
  IssueEventEdit,
} from 'shared/domain/issueEvent/issueEventChange';
import type { IssueEventInDto } from 'shared/dtos/in/issue';
import { captureDataScope, DataOwner, type DataScope } from 'serviceWorker/helpers/dataScope';

/** The issue's events in IndexedDB after a change the server saved; the pages read the issue again. */
async function updateIssueEvents(
  issueId: string,
  update: (events: EventEntity[]) => EventEntity[],
  scope: DataScope
): Promise<void> {
  await scope.write(async () => {
    const issue = await issuesDb.getOne(issueId);
    if (!issue) {
      return;
    }
    await issuesDb.updateOne(issueId, { events: update(issue.events ?? []) });
    await broadcastSavedIssue(issueId);
  });
}

function replaceEvent(events: EventEntity[], saved: IssueEventInDto): EventEntity[] {
  return events.map((event) => (event._id === saved._id ? saved : event));
}

/** Adds the event on the server and to the stored issue; resolves with the saved event. */
export async function createIssueEvent({ issueId, title, description }: IssueEventDraft): Promise<IssueEventInDto> {
  const scope = captureDataScope({ owner: DataOwner.PROJECT });
  const created = await requestProjectApi<IssueEventInDto>({
    method: 'POST',
    path: `issue/${issueId}/event`,
    body: { title, description },
  });
  await updateIssueEvents(issueId, (events) => [...events, created], scope);
  return created;
}

export async function editIssueEvent({ issueId, eventId, title, description }: IssueEventEdit): Promise<IssueEventInDto> {
  const scope = captureDataScope({ owner: DataOwner.PROJECT });
  const edited = await requestProjectApi<IssueEventInDto>({
    method: 'PUT',
    path: `issue/${issueId}/event/${eventId}`,
    body: { title, description },
  });
  await updateIssueEvents(issueId, (events) => replaceEvent(events, edited), scope);
  return edited;
}

/** Removes the event on the server; the stored issue keeps it marked as deleted, like the server. */
export async function removeIssueEvent({ issueId, eventId }: IssueEventAddress): Promise<void> {
  const scope = captureDataScope({ owner: DataOwner.PROJECT });
  await requestProjectApi<void>({ method: 'DELETE', path: `issue/${issueId}/event/${eventId}` });
  await updateIssueEvents(
    issueId,
    (events) => events.map((event) => (event._id === eventId ? { ...event, deleted: true } : event)),
    scope
  );
}
